// ============================================================
//  src/components/common/ErrorBoundary.js
//  Catches runtime errors and shows a friendly fallback
//  instead of a blank white screen.
//
//  Usage:
//    <ErrorBoundary>
//      <SomePage />
//    </ErrorBoundary>
//
//    // Custom fallback:
//    <ErrorBoundary fallback={<p>Something went wrong.</p>}>
//      <SomePage />
//    </ErrorBoundary>
// ============================================================
import React from 'react';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // In production, send to your error tracker (Sentry, etc.)
    console.error('ErrorBoundary caught:', error, info.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.props.fallback) return this.props.fallback;

    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', minHeight: '60vh',
        padding: '32px 24px', textAlign: 'center',
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%',
          background: 'rgba(255,59,48,0.08)',
          border: '1.5px solid rgba(255,59,48,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 28, marginBottom: 20,
        }}>
          <i className="fas fa-triangle-exclamation" style={{ color: 'var(--danger)' }} />
        </div>
        <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-dark)', marginBottom: 8 }}>
          Something went wrong
        </div>
        <div style={{ fontSize: 13.5, color: 'var(--text-mid)', marginBottom: 24, maxWidth: 280, lineHeight: 1.6 }}>
          An unexpected error occurred. Try refreshing the page or going back to the dashboard.
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            className="btn btn-primary"
            onClick={() => window.location.reload()}
          >
            <i className="fas fa-rotate-right" /> Reload page
          </button>
          <a href="/dashboard" className="btn btn-outline">
            Go to dashboard
          </a>
        </div>
        {process.env.NODE_ENV === 'development' && (
          <details style={{ marginTop: 24, textAlign: 'left', maxWidth: 500 }}>
            <summary style={{ fontSize: 12, color: 'var(--text-light)', cursor: 'pointer' }}>
              Error details (dev only)
            </summary>
            <pre style={{ fontSize: 11, color: 'var(--danger)', marginTop: 8, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {this.state.error?.toString()}
            </pre>
          </details>
        )}
      </div>
    );
  }
}
